import React, { useEffect, useState } from 'react';
import { CustomInput, DropDown, ToggleButton } from '../../../components/common';
import AccordionTile from '../Accordion';
import {
  Package,
  PackageProduct,
  useUpdatePackageProduct,
  useCreatePackageProduct,
  ProductTemplate,
  ProductTemplateKey,
} from '../../../hooks/useClients';
import { EmailTemplateMetadata } from '../../../hooks/useComms';

type ConsumerSubscriptionModelProps = {
  packageData: Package;
  defaultTemplate: ProductTemplate;
  setSaveDataModal: React.Dispatch<React.SetStateAction<boolean>>;
  emailTemplates?: EmailTemplateMetadata[];
};

const ConsumerSubscriptionModel: React.FunctionComponent<ConsumerSubscriptionModelProps> = ({
  packageData,
  defaultTemplate,
  setSaveDataModal,
  emailTemplates,
}): JSX.Element => {
  const selectedPackageProduct = packageData.package_products.find(
    (item: PackageProduct) => item.product.key === ProductTemplateKey.SUBSCRIPTION,
  );
  const { createPackageProduct } = useCreatePackageProduct(
    packageData.client_id!,
    selectedPackageProduct?.package_id! || packageData.id!,
    defaultTemplate.id,
  );
  const { updatePackageProduct } = useUpdatePackageProduct(packageData.client_id!, selectedPackageProduct?.id!);

  const [consumerSubscriptionModel, setConsumerSubscriptionModel] = useState(false);
  const [monthlyCost, setMonthlyCost] = useState(0.0);
  const [bonusDiscountToggle, setBonusDiscountToggle] = useState(false);
  const [minutesIncludedPerMonth, setMinutesIncludedPerMonth] = useState(0);
  const [discountedMinumumRate, setDiscountedMinimumRate] = useState<any>(0);
  const [bonusDiscount, setBonusDiscount] = useState(0);
  const [hidePaymentSetupNotice, setHidePaymentSetupNotice] = useState(true);
  const [emailLabels, setEmailLabels] = useState<any>([]);
  const [matchingTemplates, setMatchingTemplates] = useState<any>([]);
  const [selectedEmailLabel, setSelectedEmailLabel] = useState<any>();
  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  const [enableSaveOption, setEnableSaveOption] = useState(false);
  const [errorMessage, setErrorMessage] = useState<any>({});

  const getUniqueEmailId = () => {
    if (emailTemplates && emailTemplates?.length > 0) {
      const uniquieresults = emailTemplates.reduce((obj: any, item: any) => {
        const { name, id } = item;
        const label = item.labels[0] ? item.labels[0] : 'No Label';
        if (!obj[label] && label === 'No Label') {
          obj[label] = [
            {
              id: 'Do not Send Email',
              name: 'Do not Send Email',
            },
            { name, id },
          ];
        } else if (!obj[label]) {
          obj[label] = [{ name, id }];
        } else {
          obj[label] = [{ name, id }, ...obj[label]];
        }
        return obj;
      }, {});
      setEmailLabels(uniquieresults);
    }
  };

  useEffect(() => {
    setSaveDataModal(enableSaveOption);
  }, [enableSaveOption]);

  const validation = () => {
    if (consumerSubscriptionModel) {
      if (!discountedMinumumRate) {
        setErrorMessage({ discountedMinumumRate: 'Field is required' });
        return false;
      } else if (discountedMinumumRate < 0.001 || discountedMinumumRate > 2) {
        setErrorMessage({ discountedMinumumRate: 'Must be a number between 0.01 and 2.00' });
        return false;
      } else if (!minutesIncludedPerMonth) {
        setErrorMessage({ minutesinclidedPerMonth: 'Field is required' });
        return false;
      } else if (minutesIncludedPerMonth.toString().indexOf('.') !== -1) {
        setErrorMessage({
          minutesinclidedPerMonth: 'Number may not include decimals - whole numbers only',
        });
        return false;
      } else if (minutesIncludedPerMonth < 1 || minutesIncludedPerMonth > 5000) {
        setErrorMessage({ minutesinclidedPerMonth: 'Must be a number between 1 and 5000' });
        return false;
      } else if (!bonusDiscount) {
        setErrorMessage({ bonusdiscount: 'Field is required' });
        return false;
      } else if (bonusDiscount.toString().indexOf('.') !== -1) {
        setErrorMessage({
          bonusdiscount: 'Number may not include decimals - whole numbers only',
        });
        return false;
      } else if (bonusDiscount < 1 || bonusDiscount > 100) {
        setErrorMessage({ bonusdiscount: 'Must be a number between 1 and 100' });
        return false;
      }
    }
    setErrorMessage({});
    return true;
  };

  const deriveLabelFromTemplate = (template: string) => {
    if (emailTemplates && emailTemplates?.length > 0) {
      const newTemapltes = [
        {
          id: 'Do not Send Email',
          name: 'Do not Send Email',
          labels: [],
        },
        ...(emailTemplates ? emailTemplates : []),
      ];
      const templates = newTemapltes?.find((item) => item.id === template);
      return templates ? (templates.labels[0] ? templates.labels[0] : 'No Label') : 'No Label';
    }
  };

  const onSave = () => {
    if (validation()) {
      const payload: Partial<PackageProduct> = {
        status: consumerSubscriptionModel ? 'enabled' : 'disabled',
        configuration: {
          minutes: +minutesIncludedPerMonth,
          rate: discountedMinumumRate * 100,
          auto_renew: true,
          discount_enabled: bonusDiscountToggle,
          additional_discount: bonusDiscount / 100,
          email_template: selectedTemplate === 'Do not Send Email' || !selectedTemplate ? null : selectedTemplate,
        },
      };
      if (selectedPackageProduct) {
        updatePackageProduct.mutate(payload);
      } else {
        createPackageProduct.mutate(payload);
      }
    }
  };

  useEffect(() => {
    if (packageData && Object.keys(emailLabels)?.length > 0) {
      if (selectedPackageProduct) {
        setConsumerSubscriptionModel(selectedPackageProduct?.status === 'enabled');

        const configuration = selectedPackageProduct.configuration;
        const monthlyCost = configuration.minutes * (configuration.rate! / 100);
        const EmailLabel = configuration.email_template
          ? deriveLabelFromTemplate(configuration.email_template)
          : 'No Label';
        setMonthlyCost(monthlyCost);
        setHidePaymentSetupNotice(configuration?.auto_renew!);
        setMinutesIncludedPerMonth(configuration?.minutes);
        setDiscountedMinimumRate(configuration?.rate! / 100);
        setBonusDiscountToggle(configuration?.discount_enabled!);
        setBonusDiscount(configuration?.additional_discount! * 100);
        setSelectedEmailLabel(EmailLabel);
        setMatchingTemplates(emailLabels[EmailLabel!]);
        setSelectedTemplate(configuration.email_template ? configuration.email_template : 'Do not Send Email');
      } else {
        setConsumerSubscriptionModel(false);
        const configuration = defaultTemplate.configuration;
        const monthlyCost = configuration.minutes * (configuration.rate! / 100);
        setMonthlyCost(monthlyCost);
        setHidePaymentSetupNotice(configuration?.auto_renew!);
        setMinutesIncludedPerMonth(configuration?.minutes);
        setDiscountedMinimumRate(configuration?.rate! / 100);
        setBonusDiscountToggle(configuration?.discount_enabled!);
        setSelectedEmailLabel('No Label');
        setMatchingTemplates(emailLabels['No Label']);
        setSelectedTemplate('Do not Send Email');
        setBonusDiscount(configuration?.additional_discount! * 100);
      }
      setEnableSaveOption(false);
    }
  }, [packageData, emailLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getUniqueEmailId();
  }, [emailTemplates]);

  useEffect(() => {
    setMonthlyCost(minutesIncludedPerMonth * discountedMinumumRate);
  }, [minutesIncludedPerMonth, discountedMinumumRate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AccordionTile
      setStatus={(data: boolean) => {
        if ((!data && validation()) || data) {
          !enableSaveOption && setEnableSaveOption(true);
          setConsumerSubscriptionModel(data);
        }
      }}
      Label={'CONSUMER SUBSCRIPTION MODEL'}
      buttonText={'SAVE'}
      status={consumerSubscriptionModel}
      id={'consumerSubscriptionModel'}
      initialstatus={true}
      disableButton={enableSaveOption}
      onButtonClick={() => {
        if (enableSaveOption) onSave();
      }}
      InnerComponent={
        <div className="my-10 flex justify-evenly gap-7">
          <div className="w-full">
            <div className="py-4">
              <CustomInput
                inputType={'text'}
                disabled={true}
                value={'$ ' + monthlyCost.toFixed(2)}
                setValue={(data) => {
                  !enableSaveOption && setEnableSaveOption(true);
                  setMonthlyCost(data);
                }}
                Label={'Monthly Cost'}
                dataTestId="monthly-cost"
              />
            </div>
            <div className="py-4">
              <CustomInput
                inputType={'number'}
                value={minutesIncludedPerMonth}
                setValue={(data) => {
                  !enableSaveOption && setEnableSaveOption(true);
                  setMinutesIncludedPerMonth(data);
                }}
                Label={'Minutes Included/Month'}
                Error={errorMessage?.minutesinclidedPerMonth?.length > 0 ? true : false}
                ErrorMessage={errorMessage?.minutesinclidedPerMonth}
                dataTestId="minutes-included-per-month"
              />
            </div>
            <div className="py-4">
              <div className="py-4">
                <label className="text-blue-dark mb-2 block text-sm font-medium leading-4">
                  Discounted Minute Rate
                </label>
                <input
                  type={'text'}
                  value={discountedMinumumRate}
                  className="text-gray-dark bg-gray-background-light w-full rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:ring-transparent"
                  onChange={(e: any) => {
                    const validNumber = new RegExp(/^\d*\.?\d{0,3}$/);
                    const data = e.target.value;
                    if (!validNumber.test(data)) {
                      e.preventDefault();
                    } else {
                      !enableSaveOption && setEnableSaveOption(true);
                      setDiscountedMinimumRate(data);
                    }
                  }}
                  // onKeyDown={(e)=>{
                  //     e.preventDefault()

                  // }}
                />
                {errorMessage?.discountedMinumumRate?.length > 0 && (
                  <label className="text-red mb-2 block pt-2 text-sm font-medium leading-4">
                    {' '}
                    {errorMessage?.discountedMinumumRate}
                  </label>
                )}
              </div>
            </div>
            <div className="flex w-full justify-between py-4">
              <div className="mx-2">
                <p className="text-blue-dark py-2 text-sm font-medium">Bonus Discount Included</p>
              </div>
              <div>
                <ToggleButton
                  status={bonusDiscountToggle}
                  setStatus={(data) => {
                    !enableSaveOption && setEnableSaveOption(true);
                    setBonusDiscountToggle(data);
                  }}
                  initialstatus={false}
                  id={'hidePaymentSetupNotice'}
                />
              </div>
            </div>
            <div className="py-4">
              <CustomInput
                inputType={'number'}
                value={bonusDiscount}
                setValue={(data) => {
                  !enableSaveOption && setEnableSaveOption(true);
                  setBonusDiscount(data);
                }}
                Label={'Bonus Discount (%)'}
                Error={errorMessage?.bonusdiscount?.length > 0 ? true : false}
                ErrorMessage={errorMessage?.bonusdiscount}
                dataTestId="bonus-discount"
              />
            </div>
          </div>
          <div className="w-full pt-4">
            <div className="flex w-full justify-between py-4">
              <div className="mx-2">
                <p className="text-blue-dark py-2 text-sm font-medium">Auto Renews Monthly</p>
              </div>
              <div>
                <ToggleButton
                  status={hidePaymentSetupNotice}
                  setStatus={(data) => {
                    !enableSaveOption && setEnableSaveOption(true);
                    setHidePaymentSetupNotice(data);
                  }}
                  initialstatus={true}
                  id={'AutorenewsMonthly'}
                />
              </div>
            </div>
            <div className="py-4">
              <div className="flex w-full justify-between">
                <div className="w-full">
                  <DropDown
                    Label={'Email Template Label'}
                    value={selectedEmailLabel}
                    setValue={(data) => {
                      !enableSaveOption && setEnableSaveOption(true);
                      setSelectedEmailLabel(data);
                      setMatchingTemplates(emailLabels[data]);
                      setSelectedTemplate(emailLabels[data][0].id);
                    }}
                  >
                    {Object.keys(emailLabels)?.length > 0 &&
                      Object.keys(emailLabels)?.map((item: any) => <option value={item}>{item}</option>)}
                  </DropDown>
                </div>
              </div>
            </div>
            <div className="py-4">
              <div className="w-full">
                <DropDown
                  Label={'Email Template '}
                  value={selectedTemplate}
                  setValue={(data) => {
                    if (!enableSaveOption) {
                      !enableSaveOption && setEnableSaveOption(true);
                    }
                    setSelectedTemplate(data);
                  }}
                >
                  {matchingTemplates?.length > 0 &&
                    matchingTemplates?.map((item: any) => <option value={item.id}>{item.name}</option>)}
                </DropDown>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default ConsumerSubscriptionModel;
